/* eslint-disable max-len */
import { useDataModel } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import {
  getMetadataAttributeData,
  getProductSpecificationData,
  getSearchModelFilterData
} from '../utils';

const { string, bool, shape, arrayOf } = proptypes;

export const useApplianceFuelType = ({ itemId, product, metadata, search }) => {
  const hasProductData = !!product;
  const hasMetadataData = !!metadata;
  const hasSearchData = !!search;

  const {
    data: productData,
    loading: productLoading,
    error: productError
  } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId || hasProductData || hasSearchData
  });

  const finalProductData = productData?.product ?? product;
  const { isSuperSku, parentId } = finalProductData?.identifiers || {};

  const {
    data: metadataData,
    loading: metadataLoading,
    error: metadataError
  } = useDataModel('metadata', {
    variables: {
      parentId
    },
    skip: !parentId || !isSuperSku || productLoading || hasMetadataData || hasSearchData
  });

  const finalMetadataData = metadataData?.metadata ?? metadata;

  const { applianceType = '', applianceCategory = '' } = !(productLoading || hasSearchData) ? getProductSpecificationData(finalProductData) : {};
  const { metadataTypes = [], metadataCategories = [] } = !(productLoading || metadataLoading || hasSearchData) ? getMetadataAttributeData(finalMetadataData) : {};
  const { filterTypes = [], filterCategories = [] } = hasSearchData ? getSearchModelFilterData(search) : {};

  const error = !!productError || !!metadataError;

  if (error) {
    console.error({ productError, metadataError });
    throw new Error('Appliance Fuel Type Error');
  }

  const types = [applianceType, ...metadataTypes, ...filterTypes].filter((type) => !!type);
  const categories = [applianceCategory, ...metadataCategories, ...filterCategories].filter((category) => !!category);

  const isDryer = categories?.includes('dryers') || categories?.includes('dryer');
  const isRange = categories?.includes('ranges') || categories?.includes('range');
  const isEither = isDryer || isRange;
  const hasDualFuel = types?.includes('dual fuel');

  return {
    types,
    categories,
    isDryer,
    isRange,
    isEither,
    hasDualFuel,
    loading: productLoading || metadataLoading,
    error
  };
};

useApplianceFuelType.propTypes = {
  itemId: string.isRequired,
  product: shape({
    itemId: string,
    dataSource: string,
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

useApplianceFuelType.defaultProps = {
  product: null,
  metadata: null,
  search: null
};
